import type {
    ProfessionalTileFragment,
    ProfessionalHomeCategoryTileFragment,
} from 'generated/graphql';
import type { SearchResultItem } from '~/modules/search';
import { appendNoIndexParam } from '~/utils/append-noindex-param';

export interface BasicThumbnail {
    Thumbnail?: ProfessionalTileFragment['Thumbnail'];
}

type professionalWithThumbnail =
    | BasicThumbnail
    | ProfessionalTileFragment
    | SearchResultItem
    | ProfessionalHomeCategoryTileFragment;

export const isBasicThumbnail = <T>(
    p: T | BasicThumbnail
): p is BasicThumbnail => {
    return (p as BasicThumbnail).Thumbnail?.desktop !== undefined;
};

const isSearchResultItem = (
    p: professionalWithThumbnail
): p is SearchResultItem => {
    return (p as SearchResultItem).PType !== undefined;
};

const isProfessionalTileFragment = (
    p: professionalWithThumbnail
): p is ProfessionalTileFragment => {
    return (
        (p as ProfessionalTileFragment).BannerImages !== undefined &&
        (p as ProfessionalTileFragment).PrimaryCategory !== undefined
    );
};

// we have many place need to maintain get the tile thumbnail from a professional
// so we create this backward-compatible function to use in all places
// doc: https://archipro.atlassian.net/wiki/spaces/AD/pages/1788313607
export const getProfessionalTileThumbnail = (
    professional: professionalWithThumbnail,
    isMobile?: boolean
): string => {
    if (isBasicThumbnail(professional)) {
        const thumbnail = isMobile
            ? professional.Thumbnail?.mobile
            : professional.Thumbnail?.desktop;
        if (thumbnail) {
            return appendNoIndexParam(thumbnail);
        }
    }

    // check if professional is a SearchResultItem
    if (isSearchResultItem(professional)) {
        const thumbs = professional.LibraryThumbs;
        const preferredThumb = isMobile
            ? thumbs?.ThumbLandscapeMobile
            : thumbs?.ThumbLandscapeDesktop;
        return appendNoIndexParam(
            preferredThumb ??
                thumbs?.Thumb_category ??
                thumbs?.Thumb_lg ??
                professional.CompanyLogos?.Thumb_lg ??
                ''
        );
    }

    // check if professional is a ProfessionalTileFragment
    if (isProfessionalTileFragment(professional)) {
        const bannerFile = professional.BannerImages[0]?.LibraryImageFile;
        const bannerThumb = isMobile
            ? bannerFile?.ThumbLandscapeMobile
            : bannerFile?.ThumbLandscapeDesktop;

        return appendNoIndexParam(
            bannerThumb ??
                professional.LibraryThumb?.ThumbImageFile?.PhotoTileDraggable ??
                ''
        );
    }

    return '';
};
